// src/pages/Orders/Orders.js - With server-side sorting
import React, { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import { orderService } from '../../services/api';
import { useAuth } from '../../contexts/AuthContext';
import {
    PlusIcon,
    MagnifyingGlassIcon,
    ArrowPathIcon,
} from '@heroicons/react/24/outline';
import { Link } from 'react-router-dom';
import DeleteConfirmationModal from '../../components/modals/DeleteConfirmationModal';
import DesktopOrdersTable from './Views/DesktopOrdersTable';
import MobileOrdersList from './Views/MobileOrdersList';
import Pagination from './Views/Pagination';

const Orders = () => {
    const { hasRole } = useAuth();
    const [orders, setOrders] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [searchTerm, setSearchTerm] = useState('');
    const [statusFilter, setStatusFilter] = useState('');
    const [sortConfig, setSortConfig] = useState({
                                                     key: 'order_date',
                                                     direction: 'desc'
                                                 });
    const [deleteModalOpen, setDeleteModalOpen] = useState(false);
    const [orderToDelete, setOrderToDelete] = useState(null);
    const [expandedRows, setExpandedRows] = useState([]);

    // Pagination state
    const [currentPage, setCurrentPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);

    const fetchOrders = async (pageNumber = 1) => {
        try {
            setLoading(true);
            const params = {
                page: pageNumber,
                sort_by: sortConfig.key,
                sort_direction: sortConfig.direction
            };

            if (statusFilter) {
                params.status = statusFilter;
            }

            if (searchTerm && searchTerm.length >= 3) {
                params.search = searchTerm;
            }

            const response = await orderService.getOrders(params);

            // Set orders directly from the API response
            setOrders(response.data?.data || []);

            // Set pagination information from API
            setTotalPages(response.data?.last_page || 1);
            setCurrentPage(response.data?.current_page || 1);

            setError(null);
        } catch (err) {
            console.error('Error fetching orders:', err);
            setError('Failed to load orders. Please try again later.');
            toast.error('Failed to load orders');
        } finally {
            setLoading(false);
        }
    };

    // Fetch orders when status filter, sort config, or search term changes
    useEffect(() => {
        // Only trigger search if term is empty or at least 3 characters
        if (searchTerm === '' || searchTerm.length >= 3) {
            fetchOrders(1); // Reset to page 1 when filter changes
        }
    }, [statusFilter, sortConfig, searchTerm]);

    // Sort orders (now using server-side sorting)
    const requestSort = (key) => {
        let direction = 'asc';
        if (sortConfig.key === key && sortConfig.direction === 'asc') {
            direction = 'desc';
        }
        setSortConfig({ key, direction });
        // The fetch will happen in the useEffect above
    };

    // Toggle row expansion
    const toggleRowExpansion = (orderId) => {
        setExpandedRows(prevExpandedRows =>
                            prevExpandedRows.includes(orderId)
                            ? prevExpandedRows.filter(id => id !== orderId)
                            : [...prevExpandedRows, orderId]
        );
    };

    // Handle page change
    const handlePageChange = (pageNumber) => {
        if (pageNumber >= 1 && pageNumber <= totalPages) {
            fetchOrders(pageNumber);
        }
    };

    // Delete order handler
    const handleDeleteClick = (order) => {
        setOrderToDelete(order);
        setDeleteModalOpen(true);
    };

    // Confirm delete order
    const confirmDelete = async () => {
        if (!orderToDelete) return;

        try {
            await orderService.deleteOrder(orderToDelete.id);
            // After deleting, refresh current page
            fetchOrders(currentPage);
            toast.success(`Order ${orderToDelete.order_number} deleted successfully`);
        } catch (error) {
            console.error('Error deleting order:', error);
            toast.error(error.response?.data?.message || 'Failed to delete order');
        } finally {
            setDeleteModalOpen(false);
            setOrderToDelete(null);
        }
    };

    // Get status badge color (shared between desktop and mobile)
    const getStatusBadgeColor = (status) => {
        switch (status) {
            case 'pending':
                return 'bg-yellow-100 text-yellow-800';
            case 'processing':
                return 'bg-blue-100 text-blue-800';
            case 'fulfilled':
                return 'bg-green-100 text-green-800';
            case 'cancelled':
                return 'bg-red-100 text-red-800';
            default:
                return 'bg-gray-100 text-gray-800';
        }
    };

    if (loading) {
        return (
            <div className="flex items-center justify-center h-full">
                <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary-500"></div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="bg-red-100 border-l-4 border-red-500 text-red-700 p-4 rounded shadow-md">
                <p className="font-bold">Error</p>
                <p>{error}</p>
            </div>
        );
    }

    return (
        <div className="space-y-6">
            <div className="flex justify-between items-center">
                <h1 className="text-2xl font-semibold text-gray-800">Orders</h1>

                {hasRole('admin') && (
                    <Link
                        to="/orders/new"
                        className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
                    >
                        <PlusIcon className="h-5 w-5 mr-2" /> New Order
                    </Link>
                )}
            </div>

            {/* Search and filters */}
            <div className="bg-white p-4 rounded-lg shadow">
                <div className="flex flex-col md:flex-row items-center space-y-4 md:space-y-0 md:space-x-4">
                    <div className="w-full md:w-1/3">
                        <label htmlFor="search" className="sr-only">
                            Search
                        </label>
                        <div className="relative">
                            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                <MagnifyingGlassIcon className="h-5 w-5 text-gray-400" />
                            </div>
                            <input
                                id="search"
                                name="search"
                                type="text"
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                                className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md leading-5 bg-white placeholder-gray-500 focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
                                placeholder="Search orders by number or customer"
                            />
                        </div>
                    </div>

                    <div className="w-full md:w-1/4">
                        <label htmlFor="statusFilter" className="sr-only">
                            Status
                        </label>
                        <select
                            id="statusFilter"
                            name="statusFilter"
                            value={statusFilter}
                            onChange={(e) => setStatusFilter(e.target.value)}
                            className="block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm rounded-md"
                        >
                            <option value="">All Statuses</option>
                            <option value="pending">Pending</option>
                            <option value="processing">Processing</option>
                            <option value="fulfilled">Fulfilled</option>
                            <option value="cancelled">Cancelled</option>
                        </select>
                    </div>

                    <div className="w-full md:w-auto">
                        <button
                            onClick={() => {
                                setSearchTerm('');
                                setStatusFilter('');
                                setSortConfig({
                                                  key: 'order_date',
                                                  direction: 'desc'
                                              });
                                fetchOrders(1); // Reset to page 1
                            }}
                            className="w-full md:w-auto inline-flex items-center px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
                        >
                            <ArrowPathIcon className="h-5 w-5 mr-2" /> Reset Filters
                        </button>
                    </div>
                </div>
            </div>

            {/* Desktop Orders Table */}
            <DesktopOrdersTable
                orders={orders}
                sortConfig={sortConfig}
                requestSort={requestSort}
                getStatusBadgeColor={getStatusBadgeColor}
                handleDeleteClick={handleDeleteClick}
                hasRole={hasRole}
                expandedRows={expandedRows}
                toggleRowExpansion={toggleRowExpansion}
            />

            {/* Mobile Orders List */}
            <MobileOrdersList
                orders={orders}
                getStatusBadgeColor={getStatusBadgeColor}
                handleDeleteClick={handleDeleteClick}
                hasRole={hasRole}
                expandedRows={expandedRows}
                toggleRowExpansion={toggleRowExpansion}
            />

            {/* Pagination */}
            {orders.length > 0 && (
                <Pagination
                    currentPage={currentPage}
                    totalPages={totalPages}
                    onPageChange={handlePageChange}
                />
            )}

            {/* Delete Confirmation Modal */}
            <DeleteConfirmationModal
                isOpen={deleteModalOpen}
                onClose={() => setDeleteModalOpen(false)}
                onConfirm={confirmDelete}
                title="Delete Order"
                message={`Are you sure you want to delete Order ${orderToDelete?.order_number}? This action cannot be undone.`}
            />
        </div>
    );
};

export default Orders;
